<template>
  <b-row>
    <b-col cols="12" class="mb-2">
      <b-row class="align-items-center mb-2">
        <b-col cols="5">
          <h4 class="mb-0 font-weight-bolder">Audit Logs</h4>
        </b-col>
        <b-col cols="7">
          <b-row>
            <b-col cols="6">
              <b-form-input
                v-model="search"
                placeholder="Search audit logs..."
              />
            </b-col>
            <b-col cols="6">
              <v-select
                v-model="category"
                :options="categories"
                :clearable="false"
                label="label"
                @input="(val) => (category = val)"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <b-card no-body>
            <b-table
              class="mt-1"
              :key="tableKey"
              :items="fetchAuditLogs"
              :fields="fields"
              show-empty
              responsive
              primary-key="id"
              thead-class="text-nowrap"
            >
              <template #cell(status)>
                <div
                  class="bg-success rounded-pill"
                  style="width: 10px; height: 10px"
                ></div>
              </template>
              <template #cell(createdTime)="{ item }">
                <b-media vertical-align="center">
                  <span class="text-nowrap">
                    {{
                      format(new Date(item.createdTime), "yyyy-MM-dd HH:mm:ss")
                    }}
                  </span>
                </b-media>
              </template>

              <template #cell(createdByFullName)="{ item }">
                <b-media vertical-align="center">
                  <span class="font-weight-bolder text-nowrap">{{
                    item.createdByFullName || "-"
                  }}</span>
                </b-media>
              </template>
              <template #cell(action)="{ item }">
                <b-media vertical-align="center">
                  <span class="text-nowrap">{{
                    formatAction(item.action)
                  }}</span>
                </b-media>
              </template>
              <template #cell(details)="{ item }">
                <b-media vertical-align="center">
                  <span class="text-nowrap">{{ item.details || "-" }}</span>
                </b-media>
              </template>
              <template #cell(targetName)="{ item }">
                <b-media vertical-align="center">
                  <span class="text-nowrap">{{ item.targetName || "-" }}</span>
                </b-media>
              </template>
              <template #cell(category)="{ item }">
                <b-media vertical-align="center">
                  <div class="d-flex align-items-center" style="gap: 5px">
                    <span class="text-nowrap">{{
                      formatCategory(item.category)
                    }}</span>
                  </div>
                </b-media>
              </template>
            </b-table>

            <!-- Pagination -->
            <div v-if="auditLogs.length > 0" class="mx-2 mb-2">
              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-start"
                >
                  <span class="text-muted">
                    Showing
                    {{
                      Math.min(
                        pagination.limit * pagination.page,
                        pagination.total
                      )
                    }}
                    to {{ pagination.total }} of audit entries
                  </span>
                </b-col>
                <!-- Pagination -->
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-end"
                >
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="pagination.total"
                    :per-page="pagination.limit"
                    first-number
                    last-number
                    class="mb-0 mt-1 mt-sm-0"
                    prev-class="prev-item"
                    next-class="next-item"
                  >
                    <template #prev-text>
                      <feather-icon icon="ChevronLeftIcon" size="18" />
                    </template>
                    <template #next-text>
                      <feather-icon icon="ChevronRightIcon" size="18" />
                    </template>
                  </b-pagination>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import Spinner from "@core/components/Spinner";
import {
  BButton,
  BCard,
  BCardText,
  BCol,
  BFormGroup,
  BFormInput,
  BIcon,
  BRow,
  BTable,
  BPagination,
  BBadge,
  BMedia,
} from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import vSelect from "vue-select";
import { debounce } from "debounce";
import { format } from "date-fns";

export default {
  components: {
    BIcon,
    BCard,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BTable,
    BCardText,
    Spinner,
    BFormGroup,
    vSelect,
    BPagination,
    BBadge,
    BMedia,
  },
  data() {
    const categories = [
      {
        value: null,
        label: "All Categories",
        icon: null,
        color: null,
      },
      {
        value: "user_management",
        label: "User Management",
        icon: "UsersIcon",
        color: "text-primary",
      },
      {
        value: "odds_management",
        label: "Odds Management",
        icon: "SignDollarIcon",
        color: "text-success",
      },
      {
        value: "customer_service",
        label: "Customer Service",
        icon: null,
        color: null,
      },
      {
        value: "system",
        label: "System",
        icon: null,
        color: null,
      },
      {
        value: "security",
        label: "Security",
        icon: null,
        color: null,
      },
      {
        value: "financial",
        label: "Financial",
        icon: null,
        color: null,
      },
    ];

    return {
      category: categories[0],
      fields: [
        { key: "status", label: "status" },
        { key: "createdTime", label: "timestamp" },
        { key: "createdByFullName", label: "user" },
        { key: "category", label: "category" },
        { key: "action", label: "action" },
        { key: "details", label: "details" },
        { key: "targetName", label: "target" },
        { key: "ipAddress", label: "IP Address" },
      ],
      currentPage: 1,
      categories,
      search: null,
      tableKey: 0,
    };
  },
  computed: {
    ...mapState("settings/user-managements/audit-logs", [
      "auditLogs",
      "loading",
      "pagination",
    ]),
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
  },
  watch: {
    search() {
      this.handleSearchDebounced();
    },
    currentPage() {
      this.updateTableKey();
    },
    category() {
      this.updateTableKey();
    },
  },
  methods: {
    ...mapActions("settings/user-managements/audit-logs", ["getAuditLogs"]),
    format,
    async fetchAuditLogs() {
      if (this.loading) return;
      const { search, currentPage: page, category } = this;
      const params = {
        search,
        page,
        category: category.value,
      };
      try {
        const data = await this.getAuditLogs(params);
        return data;
      } catch (error) {
        return [];
      }
    },
    updateTableKey() {
      this.tableKey++;
    },

    handleSearch() {
      this.updateTableKey();
    },
    filter() {
      this.updateTableKey();
    },
    formatCategory(category) {
      const found = this.categories.find((c) => c.value === category);
      return found ? found.label : category;
    },
    getIconCategory(value) {
      return this.categories.find((c) => c.value === value) || null;
    },
    formatAction(action) {
      if (!action) return "";
      return action
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
